/* eslint-disable no-redeclare */
/**
 * Partially applies arguments to a function.
 * Uses function overloads for proper type safety.
 * Note: This is different from TypeScript's built-in Partial<T> utility type.
 */

// 0 arguments provided (just returns the function)
export function Partial<A extends any[], R>(
  fn: (...args: A) => R
): (...args: A) => R

// 1 argument provided
export function Partial<A, B extends any[], R>(
  fn: (a: A, ...rest: B) => R,
  a: A
): (...rest: B) => R

// 2 arguments provided
export function Partial<A, B, C extends any[], R>(
  fn: (a: A, b: B, ...rest: C) => R,
  a: A,
  b: B
): (...rest: C) => R

// 3 arguments provided
export function Partial<A, B, C, D extends any[], R>(
  fn: (a: A, b: B, c: C, ...rest: D) => R,
  a: A,
  b: B,
  c: C
): (...rest: D) => R

// 4 arguments provided
export function Partial<A, B, C, D, E extends any[], R>(
  fn: (a: A, b: B, c: C, d: D, ...rest: E) => R,
  a: A,
  b: B,
  c: C,
  d: D
): (...rest: E) => R

// 5 arguments provided
export function Partial<A, B, C, D, E, F extends any[], R>(
  fn: (a: A, b: B, c: C, d: D, e: E, ...rest: F) => R,
  a: A,
  b: B,
  c: C,
  d: D,
  e: E
): (...rest: F) => R

// Implementation
export function Partial(
  fn: (...args: any[]) => any,
  ...givenArgs: any[]
): (...args: any[]) => any {
  return (...rest: any[]) => fn(...givenArgs, ...rest)
}
